import { Directive, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * Accessibility for links that open a new tab (Unified Code criterion 14, both design modes):
 * adds rel="noopener noreferrer" and a visually hidden "(opens in a new tab)" text.
 * Applied automatically to every <a target="_blank"> in templates of modules importing SharedModule;
 * templates need no change. The visible external-link icon is drawn by CSS in Unified mode only
 * (styles/unified-code/components/_forms.scss), so Custom mode renders exactly as before.
 */
export const EXTERNAL_LINK_TEXT_CLASS = 'uc-external-link-text';

@Directive({
  selector: 'a[target=_blank]',
})
export class ExternalLinkDirective implements OnInit, OnDestroy {
  private hint: HTMLElement;
  private subscription: Subscription;

  constructor(
    private el: ElementRef<HTMLAnchorElement>,
    private renderer: Renderer2,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const anchor = this.el.nativeElement;
    const rel = new Set((anchor.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean));
    rel.add('noopener');
    rel.add('noreferrer');
    this.renderer.setAttribute(anchor, 'rel', Array.from(rel).join(' '));

    // ngx-translate's `translate` attribute fills an EMPTY element with the key's text node; appending the
    // hint first would make the element non-empty and the translation would never render. An empty text
    // node keeps that behaviour (the directive translates text nodes and skips the hint element).
    if (!Array.from(anchor.childNodes).some((node) => node.nodeType === Node.TEXT_NODE)) {
      this.renderer.appendChild(anchor, this.renderer.createText(''));
    }

    this.hint = this.renderer.createElement('span');
    this.renderer.addClass(this.hint, 'visually-hidden');
    this.renderer.addClass(this.hint, EXTERNAL_LINK_TEXT_CLASS);
    this.renderer.appendChild(anchor, this.hint);

    this.subscription = this.translate
      .stream('unifiedForms.opensInNewTab')
      .subscribe((text: string) => this.renderer.setProperty(this.hint, 'textContent', ` (${text})`));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
